import { resolveAppSiteEntry } from "./appSiteLinks";
import {
    readDataDeletionAppId,
    readDataDeletionPlatform,
} from "./dataDeletionAppId";

/** sessionStorage flag set when iOS app opens a legal page with ?platform=app */
export const APP_LEGAL_SESSION_KEY = "pi.platform.app";
export const APP_PLATFORM_QUERY = "platform";
export const APP_PLATFORM_VALUE = "app";
/** Optional product id on `/data-deletion/request` — e.g. `word-wheel-quest`. */
export const APP_ID_QUERY = "appId";
export const APP_LEGAL_WEBVIEW_CLASS = "pi-app-legal-webview";
export const APP_MOBILE_BODY_CLASS = "is-mobile-app";

const LEGAL_PATHS = new Set(["/legal", "/privacy", "/privacy_policy", "/terms", "/terms_of_service", "/deletion-status", "/data-deletion/request"]);

/** @param {string} pathname */
function extractAppIdFromScopedPath(pathname) {
    if (!pathname || typeof pathname !== "string") return null;
    const m = pathname.match(/^\/(?:legal|privacy_policy|terms_of_service|support|marketing)\/([^/]+)$/);
    return m?.[1] ?? null;
}

/** Mobile app listing URLs — hide site chrome without requiring ?platform=app. */
export function isMobileAppScopedPublicPath(pathname) {
    const appId = extractAppIdFromScopedPath(pathname);
    if (!appId) return false;
    return resolveAppSiteEntry(appId)?.kind === "mobile";
}

/** App-scoped support URL — e.g. `/support/word_wheel_quest` (Word Wheel Quest). */
export function isSupportPath(pathname) {
    if (!pathname || typeof pathname !== "string") return false;
    return pathname === "/support" || /^\/support\/[^/]+$/.test(pathname);
}

/** App-scoped marketing URL — e.g. `/marketing/jigsaw_muse`. */
export function isMarketingPath(pathname) {
    if (!pathname || typeof pathname !== "string") return false;
    return pathname === "/marketing" || /^\/marketing\/[^/]+$/.test(pathname);
}

/** Support, marketing, or legal pages opened from a native app listing. */
export function isAppScopedPublicPath(pathname) {
    return isLegalDocumentPath(pathname) || isSupportPath(pathname) || isMarketingPath(pathname);
}

/** `/deletion-status` and `/deletion-status/:code` — opened from iOS app / direct status links. */
export function isDeletionStatusPath(pathname) {
    if (!pathname || typeof pathname !== "string") return false;
    return pathname === "/deletion-status" || pathname.startsWith("/deletion-status/");
}

/** `/data-deletion/request` — public data deletion request form (e.g. app store listings). */
export function isDataDeletionRequestPath(pathname) {
    if (!pathname || typeof pathname !== "string") return false;
    return pathname === "/data-deletion/request" || pathname.startsWith("/data-deletion/request/");
}

/** True for /legal, /privacy_policy, /terms_of_service (+ optional /{appId}), deletion-status, data-deletion/request */
export function isLegalDocumentPath(pathname) {
    if (LEGAL_PATHS.has(pathname)) {
        return true;
    }
    if (/^\/legal\/[^/]+$/.test(pathname)) {
        return true;
    }
    if (/^\/privacy_policy\/[^/]+$/.test(pathname)) {
        return true;
    }
    if (/^\/terms_of_service\/[^/]+$/.test(pathname)) {
        return true;
    }
    return isDeletionStatusPath(pathname) || isDataDeletionRequestPath(pathname);
}

function readPlatformAppFromSearch(search) {
    if (search == null || search === "") return false;
    try {
        const params =
            typeof search === "string"
                ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
                : search;
        const get =
            typeof params.get === "function"
                ? params.get.bind(params)
                : (key) => new URLSearchParams(String(search)).get(key);
        return get(APP_PLATFORM_QUERY) === APP_PLATFORM_VALUE;
    } catch {
        return false;
    }
}

/** Query string from hash fragments like `#privacy?platform=app` → `?platform=app` */
export function extractQueryFromHash(hash) {
    if (!hash || typeof hash !== "string") return "";
    const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
    const qIdx = fragment.indexOf("?");
    if (qIdx < 0) return "";
    return fragment.slice(qIdx);
}

/**
 * Detect `platform=app` from pathname search, hash query (`#privacy?platform=app`), or full href.
 * @param {{ pathname?: string, search?: string|URLSearchParams, hash?: string, href?: string }} [loc]
 */
export function readPlatformAppFromLocation(loc = {}) {
    const search = loc.search;
    if (readPlatformAppFromSearch(search)) return true;

    const hash = loc.hash ?? "";
    const hashQuery = extractQueryFromHash(hash);
    if (hashQuery && readPlatformAppFromSearch(hashQuery)) return true;

    const href = loc.href ?? "";
    if (href && /(?:\?|#|&)(?:[^#]*&)?platform=app(?:&|#|$)/i.test(href)) {
        return true;
    }

    return false;
}

/** Persist app legal viewing session (so in-app legal links can append ?platform=app). */
export function activateAppLegalSession() {
    try {
        sessionStorage.setItem(APP_LEGAL_SESSION_KEY, "1");
    } catch {
        /* private mode / WebView storage disabled */
    }
}

export function deactivateAppLegalSession() {
    try {
        sessionStorage.removeItem(APP_LEGAL_SESSION_KEY);
    } catch {
        /* ignore */
    }
}

export function isAppLegalSessionActive() {
    try {
        return sessionStorage.getItem(APP_LEGAL_SESSION_KEY) === "1";
    } catch {
        return false;
    }
}

function isNativeAppWebView() {
    return false;
}

function isIOSUserAgent() {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPod|iPad/i.test(navigator.userAgent);
}

/**
 * Minimal chrome on /data-deletion/request when opened from native app WebView,
 * ?platform=app, ?platform=google, ?platform=apple, ?platform=amazon, ?platform=samsung,
 * or ?appId=word-wheel-quest.
 */
export function shouldUseDataDeletionRequestAppShell(pathname, search, hash) {
    if (!isDataDeletionRequestPath(pathname)) return false;
    const resolvedSearch =
        search ?? (typeof window !== "undefined" ? window.location.search : "");
    const resolvedHash =
        hash ?? (typeof window !== "undefined" ? window.location.hash : "");
    const resolvedHref =
        typeof window !== "undefined" ? window.location.href : "";
    if (
        readDataDeletionPlatform(resolvedSearch)
        || readDataDeletionAppId(resolvedSearch)
    ) {
        return true;
    }
    return (
        isNativeAppWebView() ||
        readPlatformAppFromLocation({
            pathname,
            search: resolvedSearch,
            hash: resolvedHash,
            href: resolvedHref,
        })
    );
}

/**
 * Minimal chrome on /deletion-status when opened from iPhone / native app WebView
 * (e.g. /deletion-status?code=323dec4129704f40).
 */
export function shouldUseDeletionStatusAppShell(pathname, search, hash) {
    if (!isDeletionStatusPath(pathname)) return false;
    const resolvedSearch =
        search ?? (typeof window !== "undefined" ? window.location.search : "");
    const resolvedHash =
        hash ?? (typeof window !== "undefined" ? window.location.hash : "");
    const resolvedHref =
        typeof window !== "undefined" ? window.location.href : "";
    return (
        isNativeAppWebView() ||
        isIOSUserAgent() ||
        readPlatformAppFromLocation({
            pathname,
            search: resolvedSearch,
            hash: resolvedHash,
            href: resolvedHref,
        })
    );
}

/**
 * Hide site chrome for legal pages with ?platform=app, mobile app-scoped legal URLs
 * (/legal/word_wheel_quest, etc.), app-scoped support/marketing with ?platform=app,
 * or deletion-status on iPhone / in-app WebView.
 * @param {string} pathname
 * @param {URLSearchParams|string} [search]
 * @param {string} [hash]
 */
export function shouldUseLegalAppShell(pathname, search, hash) {
    if (shouldUseDeletionStatusAppShell(pathname, search, hash)) {
        return true;
    }
    if (shouldUseDataDeletionRequestAppShell(pathname, search, hash)) {
        return true;
    }
    const resolvedSearch =
        search ?? (typeof window !== "undefined" ? window.location.search : "");
    const resolvedHash =
        hash ?? (typeof window !== "undefined" ? window.location.hash : "");
    const resolvedHref =
        typeof window !== "undefined" ? window.location.href : "";
    const hasPlatformApp = readPlatformAppFromLocation({
        pathname,
        search: resolvedSearch,
        hash: resolvedHash,
        href: resolvedHref,
    });
    if (isMobileAppScopedPublicPath(pathname)) {
        if (isLegalDocumentPath(pathname)) return true;
        if ((isSupportPath(pathname) || isMarketingPath(pathname)) && hasPlatformApp) {
            return true;
        }
    }
    if ((isSupportPath(pathname) || isMarketingPath(pathname)) && hasPlatformApp) {
        return true;
    }
    if (!isLegalDocumentPath(pathname)) return false;
    return hasPlatformApp;
}

export function applyAppLegalWebViewDocumentClass(active) {
    if (typeof document === "undefined") return;
    const on = Boolean(active);
    document.documentElement.classList.toggle(APP_LEGAL_WEBVIEW_CLASS, on);
    if (document.body) {
        document.body.classList.toggle(APP_MOBILE_BODY_CLASS, on);
    }
}

/**
 * Call on every legal-route navigation (and once before React in app-webview-legal-boot.js).
 * @param {string} pathname
 * @param {URLSearchParams|string|{ get: (k: string) => string|null }} [search]
 * @param {string} [hash] — e.g. `#privacy?platform=app`
 */
export function syncAppLegalWebViewState(pathname, search, hash) {
    const resolvedPath =
        pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
    const resolvedSearch =
        search ??
        (typeof window !== "undefined" ? window.location.search : "");
    const resolvedHash =
        hash ?? (typeof window !== "undefined" ? window.location.hash : "");
    const resolvedHref =
        typeof window !== "undefined" ? window.location.href : "";

    const hasPlatformApp = readPlatformAppFromLocation({
        pathname: resolvedPath,
        search: resolvedSearch,
        hash: resolvedHash,
        href: resolvedHref,
    });

    if (isLegalDocumentPath(resolvedPath) || isSupportPath(resolvedPath) || isMarketingPath(resolvedPath)) {
        if (hasPlatformApp || isMobileAppScopedPublicPath(resolvedPath)) {
            activateAppLegalSession();
        } else if (!isDeletionStatusPath(resolvedPath) && !isDataDeletionRequestPath(resolvedPath)) {
            deactivateAppLegalSession();
        }
    }

    applyAppLegalWebViewDocumentClass(
        shouldUseLegalAppShell(resolvedPath, resolvedSearch, resolvedHash),
    );
}

/**
 * Append platform=app to internal legal links (pathname query or hash query).
 * @param {string} route — e.g. `/privacy_policy`, `/legal#privacy`
 */
export function withAppPlatformQuery(route) {
    if (!isAppLegalSessionActive() || typeof route !== "string") return route;
    if (!route.startsWith("/")) return route;

    const hashIdx = route.indexOf("#");
    const beforeHash = hashIdx >= 0 ? route.slice(0, hashIdx) : route;
    const hash = hashIdx >= 0 ? route.slice(hashIdx) : "";
    const pathname = beforeHash.split("?")[0];
    if (!isAppScopedPublicPath(pathname)) return route;

    if (route.includes(`${APP_PLATFORM_QUERY}=`)) return route;

    if (hash) {
        const sep = hash.includes("?") ? "&" : "?";
        return `${beforeHash}${hash}${sep}${APP_PLATFORM_QUERY}=${APP_PLATFORM_VALUE}`;
    }

    const sep = beforeHash.includes("?") ? "&" : "?";
    return `${beforeHash}${sep}${APP_PLATFORM_QUERY}=${APP_PLATFORM_VALUE}`;
}
